import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
  contentChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SnackbarNotificationService } from '@shared/services/snackbar-notification.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/core/services/language.service';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'emp-solutions-menu',
  templateUrl: './solutions-menu.component.html',
  styleUrls: ['./solutions-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule]
})
export class SolutionsMenuComponent implements OnInit, OnDestroy {
  public http = inject(HttpClient);
  private notificationService = inject(SnackbarNotificationService);
  private translateService = inject(TranslateService);
  private languageService = inject(LanguageService);
  private elementRef = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);

  private currentLanguage = this.languageService.language;

  readonly toggleTemplate = contentChild<TemplateRef<HTMLElement>>('toggleTemplate');

  public showMenu = false;
  public menu: {
    link: string;
    src: `assets/img/menu-icons/${MenuItem['solution']}.png`;
    label: string;
    hasAccess: boolean;
  }[];

  private subs: Subscription[] = [];
  private set sub(subscription: Subscription) {
    this.subs.push(subscription);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const clickedInsideMenu = this.elementRef.nativeElement.contains(event.target);

    if (!clickedInsideMenu) {
      this.showMenu = false;
    }
  }

  ngOnInit() {
    this.sub = this.http.get<MenuItem[]>(`${environment.solutionsApiUrl}`).subscribe({
      next: response => {
        this.menu = response.filter(item => item.solution).map(menuItem => this.formatMenuItem(menuItem));
      },
      error: () => {
        this.notificationService.error('ERROR');
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  private formatMenuItem(menuItem: MenuItem): (typeof this.menu)[number] {
    switch (menuItem.solution) {
      case 'prophet': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/prophet.png`,
          label: this.translateService.instant('SOLUTION_PROPHET_AI_PROGNOSIS'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/prophet-ai-prognosis/`
        };
      }
      case 'cockpit': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/cockpit.png`,
          label: this.translateService.instant('SOLUTION_ENERGY_COCKPIT'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/energy-cockpit/`
        };
      }
      case 'customer-analytics': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/customer-analytics.png`,
          label: this.translateService.instant('SOLUTION_CUSTOMER_ANALYTICS'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/customer-analytics/`
        };
      }
      case 'data-hub': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/data-hub.png`,
          label: this.translateService.instant('SOLUTION_DATA_HUB'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/data-hub/`
        };
      }
      case 'data-shooter': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/data-shooter.png`,
          label: this.translateService.instant('SOLUTION_DATA_SHOOTER'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/data-shooter/`
        };
      }
      case 'emo': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/emo.png`,
          label: this.translateService.instant('SOLUTION_ENERGY_MARKET_ONLINE'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/energy-market-online/`
        };
      }
      case 'energy-flex-contract': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/energy-flex-contract.png`,
          label: this.translateService.instant('SOLUTION_ENERGY_FLEX_CONTRACT'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/energy-flex-contract-manager/`
        };
      }
      case 'esmart': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/esmart.png`,
          label: this.translateService.instant('SOLUTION_ESMART'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/esmart-intelligence/`
        };
      }
      case 'flex-cockpit': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/flex-cockpit.png`,
          label: this.translateService.instant('SOLUTION_FLEX_COCKPIT'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/energy-cockpit/`
        };
      }
      case 'live-prices': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/live-prices.png`,
          label: this.translateService.instant('SOLUTION_LIVE_PRICES'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/solutions/${this.currentLanguage()}/live-prices/`
        };
      }
      case 'market-news': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/market-news.png`,
          label: this.translateService.instant('SOLUTION_MARKET_NEWS'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/home/${this.currentLanguage()}/news`
        };
      }
      case 'market-reports': {
        return {
          hasAccess: menuItem.hasAccess,
          src: `assets/img/menu-icons/market-reports.png`,
          label: this.translateService.instant('SOLUTION_MARKET_REPORTS'),
          link: menuItem.hasAccess
            ? menuItem.link
            : `https://www.energymarketprice.com/home/${this.currentLanguage()}/library`
        };
      }
      default: {
        throw Error('wrong solution');
      }
    }
  }

  public safeHtml(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }
}

type MenuItem = {
  solution:
    | 'prophet'
    | 'emo'
    | 'energy-flex-contract'
    | 'flex-cockpit'
    | 'cockpit'
    | 'esmart'
    | 'data-hub'
    | 'data-shooter'
    | 'customer-analytics'
    | 'live-prices'
    | 'market-news'
    | 'market-reports';
  link: string;
  hasAccess: boolean;
};
